import { useMemo, useState } from "react";
import { Flex } from "../../components/Flex";
import { Slider } from "../../components/Slider";
import { Image } from "../../components/Image";
import success from "../../assets/card-success-icon.svg";
import inReview from "../../assets/card-in-review-icon.svg";
import { useMonitoringActivityView } from "../../data/useMonitoringActivityView";
import { Box } from "../../components/Box";
import { H3 } from "../../components/Heading";
import { Loader } from "../../components/Loader";
import { TableBody } from "../../components/Table/TableBody";
import { TableCell } from "../../components/Table/TableCell";
import { TableHead } from "../../components/Table/TableHead";
import { TableRow } from "../../components/Table/TableRow";
import {
  IndividualCheckStatus,
  CheckStatusEnum,
} from "../../context/MandateStatusContext";
import {
  CheckContainer,
  CheckParagraph,
  CheckHeading,
  CheckTable,
  CheckTableHeaderRow,
  CheckTableCell,
  CheckTitle,
  Section,
} from "./CheckStyledComponents";
import { ManualAcceptReject } from "./ManualAcceptReject";
import { IndividualChecksMap } from "./helper";
import { CheckResults } from "./CheckResults";
import { GrayCard } from "../../components/Card";
import { Paragraph } from "../../components/Paragraph";
import { DateTime } from "luxon";
import { ApprovalComment } from "./approvalComment";
import { ExpansionPanel } from "../../components/ExpansionPanel";
import CustomTabs from "../../components/CustomTabs";
import { Modal } from "../../components/Modal";
import { Link } from "../../components/Link";

export const PepAndSanctionsMonitoring = (props) => {
  const { isOpen, onRequestClose, id, isIndividual, useSlider = true } = props;

  const { data: individualCheckData, isLoading: isLoadingIndividualCheck } =
    useMonitoringActivityView(id);

  const instance = individualCheckData?.data?.instance;

  const [selectedExternalUrl, setSelectedExternalUrl] = useState(null);

  const formatDate = (date) => {
    if (!date) return "-";
    const parts = date.split("-");
    const filteredParts = parts.filter(
      (part) => part !== "00" && part !== "0000",
    );
    return filteredParts.length ? filteredParts.join("-") : "Invalid Date";
  };

  const formatSourceDate = (date) => {
    if (!date) return "-";

    return DateTime.fromISO(date).isValid
      ? DateTime.fromISO(date).toFormat("dd/MM/yyyy")
      : date;
  };

  const getLinkLabel = (url) => url || "-";

  const openExternalLinkModal = (url) => {
    if (!url) return;
    setSelectedExternalUrl(url);
  };

  const closeExternalLinkModal = () => {
    setSelectedExternalUrl(null);
  };

  const proceedToExternalLink = () => {
    if (selectedExternalUrl) {
      window.open(selectedExternalUrl, "_blank", "noopener,noreferrer");
    }
    closeExternalLinkModal();
  };

  const getAllRecordSources = useMemo(() => {
    const groupedSources = [];

    const pushGroupedSources = (groupLabel, sourceUrls, matchingName) => {
      if (!Array.isArray(sourceUrls) || sourceUrls.length === 0) return;

      const uniqueSources = [
        ...new Map(
          sourceUrls
            .filter((item) => item?.sourceURI)
            .map((item) => [
              `${item.sourceURI}-${item.dateModified || ""}`,
              {
                sourceURI: item.sourceURI,
                dateModified: item.dateModified || null,
              },
            ]),
        ).values(),
      ];

      if (uniqueSources.length === 0) return;

      groupedSources.push({
        label: groupLabel,
        matchingName: matchingName || groupLabel,
        sources: uniqueSources.sort((a, b) => {
          const dateA = new Date(a?.dateModified || 0).getTime();
          const dateB = new Date(b?.dateModified || 0).getTime();
          return dateB - dateA;
        }),
      });
    };

    pushGroupedSources(
      "PEP Check",
      instance?.pep_check?.sourceUrls,
      instance?.pep_check?.details?.[0]?.name || "PEP Check",
    );

    pushGroupedSources(
      "PEP Excluded Result",
      instance?.pep_excluded_result?.sourceUrls,
      instance?.pep_excluded_result?.details?.[0]?.name ||
        "PEP Excluded Result",
    );

    pushGroupedSources(
      "Sanctions Check",
      instance?.sanctions_check?.sourceUrls,
      instance?.sanctions_check?.details?.[0]?.matching_name ||
        instance?.sanctions_check?.details?.[0]?.name ||
        "Sanctions Check",
    );

    pushGroupedSources(
      "Sanctions Excluded Result",
      instance?.sanctions_excluded_result?.sourceUrls,
      instance?.sanctions_excluded_result?.details?.[0]?.matching_name ||
        instance?.sanctions_excluded_result?.details?.[0]?.name ||
        "Sanctions Excluded Result",
    );

    return groupedSources;
  }, [instance]);

  const hasRecordSources = getAllRecordSources.length > 0;

  const RenderDOB = ({ dob }) => {
    if (!dob) return "-";
    const renderDate = (date) =>
      date.length > 4 && DateTime.fromISO(date).isValid
        ? DateTime.fromISO(date).toFormat("dd/MM/yyyy")
        : formatDate(date);

    return Array.isArray(dob)
      ? dob.length
        ? dob.map((d, i) => (
            <Paragraph
              key={i}
              mt={"0px"}
              mb={1}
              color="gray.80"
              fontWeight="500"
            >
              {renderDate(d)}
            </Paragraph>
          ))
        : "-"
      : renderDate(dob);
  };

  const checkResultsContent = (
    <>
      {isIndividual && instance && Object.keys(instance).length !== 0 && (
        <Section>
          {instance?.pep_check && (
            <>
              <Flex justifyContent="space-between" mb={2}>
                <Box>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Image
                      src={
                        instance?.pep_check?.status ===
                        IndividualCheckStatus.Passed
                          ? success
                          : inReview
                      }
                      alt="status icon"
                      height="20px"
                      width="auto"
                      mr={2}
                      mb={0}
                    />
                    <H3>
                      PEP Check
                      {instance?.pep_check?.status !==
                        IndividualCheckStatus.Passed && (
                        <>
                          &nbsp;-&nbsp;
                          {instance?.pep_check?.status}
                        </>
                      )}
                    </H3>
                  </div>
                </Box>
                <Box></Box>
              </Flex>

              {instance?.pep_check?.status !== IndividualCheckStatus.Passed && (
                <>
                  <Box mb={2}>
                    <CheckHeading>Details:</CheckHeading>
                    <CheckTable>
                      <TableHead></TableHead>
                      <TableBody>
                        <CheckTableHeaderRow>
                          <CheckTableCell>Code</CheckTableCell>
                          <CheckTableCell>Matching Name</CheckTableCell>
                          <CheckTableCell>Date of Birth</CheckTableCell>
                          <CheckTableCell>Reason</CheckTableCell>
                        </CheckTableHeaderRow>
                        {instance?.pep_check?.details?.map(
                          ({ code, name, dob, reason }, i) => (
                            <TableRow key={i}>
                              <TableCell
                                color="gray.60"
                                borderBottom="none"
                                verticalAlign="top"
                              >
                                {code || "-"}
                              </TableCell>
                              <TableCell
                                color="gray.60"
                                borderBottom="none"
                                verticalAlign="top"
                              >
                                {name || "-"}
                              </TableCell>
                              <TableCell
                                color="gray.60"
                                borderBottom="none"
                                verticalAlign="top"
                              >
                                <RenderDOB dob={dob} />
                              </TableCell>
                              <TableCell
                                color="gray.60"
                                borderBottom="none"
                                verticalAlign="top"
                              >
                                {reason || "-"}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </CheckTable>
                  </Box>
                </>
              )}

              {instance?.pep_check?.status === IndividualCheckStatus.Passed && (
                <>
                  <Flex alignItems="center" mb={2}>
                    <CheckHeading>Result: &nbsp;</CheckHeading>
                    <CheckParagraph>PASSED</CheckParagraph>
                  </Flex>
                  <GrayCard mx="0">
                    <Paragraph fontWeight="600" m="0">
                      Supplied details did not match
                    </Paragraph>
                  </GrayCard>
                </>
              )}
            </>
          )}

          {instance &&
            instance?.pep_excluded_result &&
            instance?.pep_excluded_result?.details.length > 0 && (
              <ExpansionPanel title="Excluded results">
                <CheckTable>
                  <TableHead>
                    <CheckTableCell>Code</CheckTableCell>
                    <CheckTableCell>Matching Name</CheckTableCell>
                    <CheckTableCell>Date of Birth</CheckTableCell>
                    <CheckTableCell>Reason</CheckTableCell>
                    <CheckTableCell>Excluded reason</CheckTableCell>
                  </TableHead>
                  <TableBody>
                    {instance?.pep_excluded_result?.details?.map(
                      ({ code, name, dob, reason, excluded_reason }, i) => (
                        <TableRow key={i}>
                          <TableCell
                            color="gray.60"
                            borderBottom="none"
                            verticalAlign="top"
                          >
                            {code || "-"}
                          </TableCell>
                          <TableCell
                            color="gray.60"
                            borderBottom="none"
                            verticalAlign="top"
                          >
                            {name || "-"}
                          </TableCell>
                          <TableCell
                            minWidth={120}
                            color="gray.60"
                            borderBottom="none"
                            verticalAlign="top"
                          >
                            <RenderDOB dob={dob} />
                          </TableCell>
                          <TableCell
                            color="gray.60"
                            borderBottom="none"
                            verticalAlign="top"
                          >
                            {reason || "-"}
                          </TableCell>
                          <TableCell
                            color="gray.60"
                            borderBottom="none"
                            verticalAlign="top"
                          >
                            {excluded_reason?.join(", ") || "-"}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </CheckTable>
              </ExpansionPanel>
            )}
        </Section>
      )}

      {instance && Object.keys(instance).length !== 0 && (
        <Section>
          {instance?.sanctions_check && (
            <>
              <Flex justifyContent="space-between" mb={2}>
                <Box>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Image
                      src={
                        instance?.sanctions_check?.status ===
                        IndividualCheckStatus.Passed
                          ? success
                          : inReview
                      }
                      alt="status icon"
                      height="20px"
                      width="auto"
                      mr={2}
                      mb={0}
                    />
                    <H3>
                      Sanctions Check
                      {instance?.sanctions_check?.status !==
                        IndividualCheckStatus.Passed && (
                        <>
                          &nbsp;-&nbsp;
                          {instance?.sanctions_check?.status}
                        </>
                      )}
                    </H3>
                  </div>
                </Box>
                <Box></Box>
              </Flex>

              {instance?.sanctions_check?.status !==
                IndividualCheckStatus.Passed &&
                instance?.sanctions_check?.details &&
                instance?.sanctions_check?.details.length > 0 && (
                  <>
                    <Box mb={2}>
                      <CheckHeading>Details:</CheckHeading>
                    </Box>
                    {instance?.sanctions_check?.details.map((detail, index) => (
                      <GrayCard key={index} mx="0px">
                        <H3
                          mb={2}
                          py={2}
                          borderBottom="1px solid"
                          borderBottomColor="gray.30"
                        >
                          {detail?.status}{" "}
                          {detail?.code === "WLT"
                            ? "- WatchList"
                            : detail?.code === "SAN"
                            ? "- Sanction"
                            : detail?.code
                            ? `- ${detail.code}`
                            : ""}
                        </H3>

                        <Flex justifyContent="space-between">
                          <Box flex={1}>
                            <CheckHeading color="gray.60" fontWeight="500">
                              Matching Name
                            </CheckHeading>
                            <Paragraph
                              mt={1}
                              mb={2}
                              color="gray.80"
                              fontWeight="500"
                            >
                              {detail?.matching_name
                                ? detail?.matching_name
                                : "-"}
                            </Paragraph>
                          </Box>

                          {isIndividual && (
                            <Box flex={1}>
                              <CheckHeading color="gray.60" fontWeight="500">
                                Date of Birth
                              </CheckHeading>
                              <Box mt={1} mb={2}>
                                <RenderDOB dob={detail.dob} />
                              </Box>
                            </Box>
                          )}
                        </Flex>

                        <Flex justifyContent="space-between">
                          <Box flex={1}>
                            <CheckHeading color="gray.60" fontWeight="500">
                              Source Name
                            </CheckHeading>
                            <Paragraph
                              mt={1}
                              mb={2}
                              color="gray.80"
                              fontWeight="500"
                            >
                              {detail?.source_name ? detail?.source_name : "-"}
                            </Paragraph>
                          </Box>

                          <Box flex={1}>
                            <CheckHeading color="gray.60" fontWeight="500">
                              Date
                            </CheckHeading>
                            <Paragraph
                              mt={1}
                              mb={2}
                              color="gray.80"
                              fontWeight="500"
                            >
                              {detail?.date
                                ? DateTime.fromISO(detail.date).toFormat(
                                    "dd/MM/yyyy",
                                  )
                                : "-"}
                            </Paragraph>
                          </Box>
                        </Flex>

                        <Box>
                          <CheckHeading color="gray.60" fontWeight="500">
                            Reason
                          </CheckHeading>
                          <Paragraph
                            mt={1}
                            mb={2}
                            color="gray.80"
                            fontWeight="500"
                          >
                            {detail?.reason ? detail.reason : "-"}
                          </Paragraph>
                        </Box>
                      </GrayCard>
                    ))}
                  </>
                )}

              {instance?.sanctions_check?.status ===
                IndividualCheckStatus.Passed && (
                <>
                  <Flex alignItems="center" mb={2}>
                    <CheckHeading>Result: &nbsp;</CheckHeading>
                    <CheckParagraph>PASSED</CheckParagraph>
                  </Flex>
                  <GrayCard mx="0">
                    <Paragraph fontWeight="600" m="0">
                      Supplied details did not match
                    </Paragraph>
                  </GrayCard>
                </>
              )}
            </>
          )}

          {instance?.sanctions_excluded_result &&
            instance?.sanctions_excluded_result?.details.length > 0 && (
              <ExpansionPanel title="Excluded results">
                {instance?.sanctions_excluded_result?.details.map(
                  (detail, index) => (
                    <GrayCard key={index} mx="0px" backgroundColor={"pink.100"}>
                      <H3
                        mb={2}
                        py={2}
                        borderBottom="1px solid"
                        borderBottomColor="gray.30"
                      >
                        {detail?.status}{" "}
                        {detail?.code === "WLT"
                          ? "- WatchList"
                          : detail?.code === "SAN"
                          ? "- Sanction"
                          : detail?.code
                          ? `- ${detail.code}`
                          : ""}
                      </H3>

                      <Flex justifyContent="space-between">
                        <Box flex={1}>
                          <CheckHeading color="gray.60" fontWeight="500">
                            Matching Name
                          </CheckHeading>
                          <Paragraph
                            mt={1}
                            mb={2}
                            color="gray.80"
                            fontWeight="500"
                          >
                            {detail?.matching_name
                              ? detail?.matching_name
                              : "-"}
                          </Paragraph>
                        </Box>

                        {isIndividual && (
                          <Box flex={1}>
                            <CheckHeading color="gray.60" fontWeight="500">
                              Date of Birth
                            </CheckHeading>
                            <Box mt={1} mb={2}>
                              <RenderDOB dob={detail.dob} />
                            </Box>
                          </Box>
                        )}
                      </Flex>

                      <Flex justifyContent="space-between">
                        <Box flex={1}>
                          <CheckHeading color="gray.60" fontWeight="500">
                            Source Name
                          </CheckHeading>
                          <Paragraph
                            mt={1}
                            mb={2}
                            color="gray.80"
                            fontWeight="500"
                          >
                            {detail?.source_name ? detail?.source_name : "-"}
                          </Paragraph>
                        </Box>

                        <Box flex={1}>
                          <CheckHeading color="gray.60" fontWeight="500">
                            Date
                          </CheckHeading>
                          <Paragraph
                            mt={1}
                            mb={2}
                            color="gray.80"
                            fontWeight="500"
                          >
                            {detail?.date
                              ? DateTime.fromISO(detail?.date).toFormat(
                                  "dd/MM/yyyy",
                                )
                              : "-"}
                          </Paragraph>
                        </Box>
                      </Flex>

                      <Box>
                        <CheckHeading color="gray.60" fontWeight="500">
                          Reason
                        </CheckHeading>
                        <Paragraph
                          mt={1}
                          mb={2}
                          color="gray.80"
                          fontWeight="500"
                        >
                          {detail?.reason ? detail?.reason : "-"}
                        </Paragraph>
                      </Box>

                      {detail?.excluded_reason &&
                        detail?.excluded_reason.length > 0 && (
                          <Box
                            borderTop={"1px solid"}
                            borderTopColor={"gray.100"}
                          >
                            <Paragraph fontWeight={"bold"} mb={3}>
                              Excluded reason
                            </Paragraph>
                            <Paragraph mb={1} fontWeight={"500"}>
                              {detail.excluded_reason.join(", ")}
                            </Paragraph>
                          </Box>
                        )}
                    </GrayCard>
                  ),
                )}
              </ExpansionPanel>
            )}
        </Section>
      )}
    </>
  );

  const positionsContent = (
    <>
      <Section>
        <H3 mb={2} ml={1}>
          Positions
        </H3>
        {instance?.pep_check?.positions &&
        instance?.pep_check?.positions.length > 0 ? (
          <CheckTable>
            <TableHead></TableHead>
            <TableBody>
              <CheckTableHeaderRow>
                <CheckTableCell>Position</CheckTableCell>
                <CheckTableCell>Start</CheckTableCell>
                <CheckTableCell>End</CheckTableCell>
              </CheckTableHeaderRow>
              {instance?.pep_check?.positions?.map(
                ({ position, from_year, to_year }, i) => (
                  <TableRow key={i}>
                    <TableCell
                      color="gray.60"
                      borderBottom="none"
                      verticalAlign="top"
                      width="60%"
                    >
                      {position || "-"}
                    </TableCell>
                    <TableCell
                      color="gray.60"
                      borderBottom="none"
                      verticalAlign="top"
                      width="20%"
                    >
                      {from_year || "-"}
                    </TableCell>

                    <TableCell
                      color="gray.60"
                      borderBottom="none"
                      verticalAlign="top"
                      width="20%"
                    >
                      {to_year || "-"}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </CheckTable>
        ) : (
          <Paragraph>No positions found</Paragraph>
        )}
      </Section>
    </>
  );

  const relationsContent = (
    <>
      {instance && Object.keys(instance).length !== 0 && (
        <>
          <Section>
            <H3 mb={2} ml={1}>
              Relations
            </H3>
            {instance?.pep_check?.relations?.length > 0 ? (
              <CheckTable>
                <TableHead></TableHead>
                <TableBody>
                  <CheckTableHeaderRow>
                    <CheckTableCell>Relation Type</CheckTableCell>
                    <CheckTableCell>Name</CheckTableCell>
                  </CheckTableHeaderRow>
                  {instance?.pep_check?.relations.map(
                    ({ entityName, relationType }, i) => (
                      <TableRow key={i}>
                        <TableCell borderBottom="none" verticalAlign="top">
                          {relationType || "-"}
                        </TableCell>
                        <TableCell
                          color="gray.60"
                          borderBottom="none"
                          verticalAlign="top"
                        >
                          {entityName || "-"}
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </CheckTable>
            ) : (
              <Paragraph>No relations found</Paragraph>
            )}
          </Section>
        </>
      )}
    </>
  );

  const aliases = isIndividual
    ? instance?.pep_check?.aliases
    : instance?.sanctions_check?.aliases;

  const aliasesContent = (
    <Section>
      <H3 mb={2}>Aliases</H3>
      {aliases && aliases.length > 0 ? (
        <CheckTable>
          <TableHead />
          <TableBody>
            <CheckTableHeaderRow>
              <CheckTableCell>Type</CheckTableCell>
              <CheckTableCell>Value</CheckTableCell>
            </CheckTableHeaderRow>
            {aliases.map(({ type, name }, i) => (
              <TableRow key={i}>
                <TableCell borderBottom="none" verticalAlign="top">
                  {type || "-"}
                </TableCell>
                <TableCell
                  color="gray.60"
                  borderBottom="none"
                  verticalAlign="top"
                >
                  {name || "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </CheckTable>
      ) : (
        <Paragraph>No aliases found</Paragraph>
      )}
    </Section>
  );

  const recordSourcesContent = (
    <Section>
      <H3 mb={2}>Sources</H3>

      {hasRecordSources ? (
        getAllRecordSources.map((group, groupIndex) => (
          <Box key={`${group.label}-${groupIndex}`} mb={4}>
            <ExpansionPanel
              defaultExpanded={groupIndex === 0}
              title={group.label}
            >
              <CheckTable>
                <TableHead></TableHead>
                <TableBody>
                  <CheckTableHeaderRow>
                    <CheckTableCell>Date Modified</CheckTableCell>
                    <CheckTableCell>URL</CheckTableCell>
                  </CheckTableHeaderRow>

                  {group.sources.map((source, index) => (
                    <TableRow key={`${source.sourceURI}-${index}`}>
                      <TableCell
                        color="gray.60"
                        borderBottom="none"
                        verticalAlign="top"
                        width="25%"
                      >
                        {formatSourceDate(source?.dateModified)}
                      </TableCell>

                      <TableCell
                        color="gray.60"
                        borderBottom="none"
                        verticalAlign="top"
                        style={{ wordBreak: "break-all" }}
                      >
                        {source?.sourceURI ? (
                          <Link
                            type="button"
                            onClick={() =>
                              openExternalLinkModal(source.sourceURI)
                            }
                          >
                            {getLinkLabel(source.sourceURI)}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </CheckTable>
            </ExpansionPanel>
          </Box>
        ))
      ) : (
        <Paragraph>No record sources found</Paragraph>
      )}
    </Section>
  );

  const externalLinkModal = (
    <Modal
      isOpen={!!selectedExternalUrl}
      onClose={closeExternalLinkModal}
      onModalConfirmClick={proceedToExternalLink}
      title="Open source url in a new tab"
      content={
        selectedExternalUrl
          ? `This source url will open in a new browser tab.
           You'll stay on the source page, so you can come back anytime.
           `
          : ""
      }
      primaryButtonText="Continue"
      secondaryButtonText="Cancel"
      hasSecondaryButton
      primaryButtonVariant="primary"
      secondaryButtonVariant="text"
      width="420px"
    />
  );

  const content = (
    <>
      <CheckContainer>
        <Flex justifyContent="space-between">
          <CheckTitle>{individualCheckData?.data?.event}</CheckTitle>
          {individualCheckData?.data?.status?.status ===
            CheckStatusEnum.Review && (
            <ManualAcceptReject
              id={id}
              title={IndividualChecksMap["pep_&_sanctions_check"].title}
              isMonitoring={true}
              permissionCodename="monitoring.review_activity"
              permissionDeniedMessage="You don't have permission to accept or reject this monitoring alert"
            />
          )}
        </Flex>

        {individualCheckData?.data?.status?.status && (
          <CheckResults status={individualCheckData?.data?.status?.status} />
        )}

        {instance?.approval_comment && (
          <ApprovalComment
            data={instance?.approval_comment}
            status={individualCheckData?.data?.status?.status}
          />
        )}

        <CustomTabs
          tabs={[
            { label: "Check results", content: checkResultsContent },
            { label: "Positions", content: positionsContent },
            { label: "Relations", content: relationsContent },
            { label: "Aliases", content: aliasesContent },
            ...(hasRecordSources
              ? [{ label: "Sources", content: recordSourcesContent }]
              : []),
          ]}
          customMarginLeft="24px"
        />
      </CheckContainer>

      {externalLinkModal}
    </>
  );

  return isLoadingIndividualCheck ? (
    <Flex justifyContent="center" alignItems="center" minHeight="60vh">
      <Loader />
    </Flex>
  ) : useSlider ? (
    <Slider
      isOpen={isOpen}
      onRequestClose={() => onRequestClose(true)}
      width="70%"
    >
      {content}
    </Slider>
  ) : (
    content
  );
};
